import { minutesToMilliseconds } from 'date-fns';
import ClientStorage, { IClientStorageOptions } from './base/client-storage';
import { defaultLang } from '@src/i18n';
const productOverviewSeenKey = 'product-overview-seen';
const localLanguageSelector = 'local-language-selector';
const historyDashboardAnnonucementPostponeKey = 'history-dashboard-announcement-postpone';
const isHistoryAnnouncmentSeenKey = 'history-announcement-seen';
const isEtaAnnouncementSeenKey = 'eta-announcement-seen';
const orderCardInitialTutorialSeenKey = 'order-card-initial-tutorial-seen';
const manageCourierSettingsTutorialSeenKey = 'manage-courier-settings-tutorial-seen';
const manageCourierOrderHideTutorialSeenKey = 'manage-courier-order-hide-tutorial-seen';

const onboardTutorialSeenKey = 'onboard-tutorial-seen';
const notificationPermissionConsent = 'notification-permission-consent';
const PWATutorialPostpone = 'pwa-tutorial-postpone';
const PWATutorialPostponeCounter = 'pwa-tutorial-postpone-counter';
const ETABannerPostponeCounter = 'eta-banner-postpone-counter';
const statisticsTutorialSeenKey = 'statistics-tutorial-seen';
const clusterBannerSeenKey = 'cluster-banner-seen';

const notificationGrantType = 'notification-grant-type';
interface ILocalLanguageStore {
  storedLanguageCode?: string;
  languageSetTime?: number;
}

export enum NotificationConsent {
  SHOW_LATER = 'later',
  DONT_SHOW = 'never',
  CONSENT_FULL = 'full',
  SHOW_NEXT_DAY = 'next',
  ON_REFRESH = 'on_refresh',
}

class UtilStorageService extends ClientStorage {
  constructor(options?: IClientStorageOptions) {
    super(options);
  }

  public setProductOverviewSeen = () => {
    this.saveToClientStorage(productOverviewSeenKey, true, 63072000000 /* 2 years */);
  };

  public setHistoryAnnouncementSeen = () => {
    this.saveToClientStorage(isHistoryAnnouncmentSeenKey, true, 63072000000 /* 2 years */);
  };

  public setEtaAnnouncementSeen = () => {
    this.saveToClientStorage(isEtaAnnouncementSeenKey, true, 63072000000 /* 2 years */);
  };

  public setLocalLanguageSelector(data: Partial<ILocalLanguageStore>) {
    this.saveToClientStorage(localLanguageSelector, data);
  }

  private getProductOverviewSeen = () => {
    return this.getStoredItem(productOverviewSeenKey);
  };

  private getHistoryAnnouncementSeen = () => {
    return this.getStoredItem(isHistoryAnnouncmentSeenKey);
  };

  private getEtaAnnouncementSeen = () => {
    return this.getStoredItem(isEtaAnnouncementSeenKey);
  };

  public setOrderCardInitialTutorialSeen = () => {
    return this.saveToClientStorage(
      orderCardInitialTutorialSeenKey,
      true,
      minutesToMilliseconds(60 * 24 * 1000000)
    );
  };

  public getOrderCardInitialTutorialSeen = () => {
    return this.getStoredItem(orderCardInitialTutorialSeenKey);
  };

  public setManageCourierSettingsTutorialSeen = (value: string | boolean) => {
    return this.saveToClientStorage(
      manageCourierSettingsTutorialSeenKey,
      value,
      minutesToMilliseconds(60 * 24 * 1000000)
    );
  };

  public getManageCourierSettingsTutorialSeen = () => {
    return this.getStoredItem(manageCourierSettingsTutorialSeenKey);
  };

  public setManageCourierOrderHideTutorialSeen = (value: string | boolean) => {
    return this.saveToClientStorage(
      manageCourierOrderHideTutorialSeenKey,
      value,
      minutesToMilliseconds(60 * 24 * 1000000)
    );
  };

  public getManageCourierOrderHideTutorialSeen = () => {
    return this.getStoredItem(manageCourierOrderHideTutorialSeenKey);
  };

  public setOnboardingTutorialSeen = (value: string | boolean) => {
    return this.saveToClientStorage(
      onboardTutorialSeenKey,
      value,
      minutesToMilliseconds(60 * 24 * 1000000)
    );
  };

  public getOnboardingTutorialSeen = () => {
    return this.getStoredItem(onboardTutorialSeenKey);
  };

  public setStatisticsTutorialSeen = (value: string | boolean) => {
    return this.saveToClientStorage(
      statisticsTutorialSeenKey,
      value,
      minutesToMilliseconds(60 * 24 * 1000000)
    );
  };

  public getStatisticsTutorialSeen = () => {
    return this.getStoredItem(statisticsTutorialSeenKey);
  };

  private getLocalSelectedLanguageData = () => {
    return this.getStoredItem(localLanguageSelector);
  };

  public getNotificationPermissionConsent = () => {
    return this.getStoredItem(notificationPermissionConsent);
  };

  public getNotificationGrantType = () => {
    return this.getStoredItem(notificationGrantType);
  };

  public setClusterBannerSeen = (data?: any) => {
    return this.saveToClientStorage(clusterBannerSeenKey, data);
  };

  public getClusterBannerSeen = () => {
    return this.getStoredItem(clusterBannerSeenKey);
  };

  public setNotificationPermissionConsent = (data: string) => {
    const storageTime =
      data === NotificationConsent.SHOW_NEXT_DAY
        ? PWA_PERMISSION.SHOW_LATER
        : minutesToMilliseconds(60 * 24 * 1000000);
    return this.saveToClientStorage(notificationPermissionConsent, data, storageTime);
  };

  public setNotificationGrantType = (data?: any) => {
    return this.saveToClientStorage(notificationGrantType, data);
  };

  public readLocalLanguageData = (): Partial<ILocalLanguageStore> => {
    if (!this.getLocalSelectedLanguageData()) {
      return {
        storedLanguageCode: defaultLang,
        languageSetTime: 0,
      };
    }
    const { storedLanguageCode, languageSetTime } = this.getLocalSelectedLanguageData();
    return {
      storedLanguageCode,
      languageSetTime,
    };
  };

  public isProductOverviewAlreadySeen = (): boolean => {
    return this.getProductOverviewSeen() === true;
  };

  public isHistoryAnnouncementAlreadySeen = (): boolean => {
    return this.getHistoryAnnouncementSeen() === true;
  };

  public isEtaAnnouncementAlreadySeen = (): boolean => {
    return this.getEtaAnnouncementSeen() === true;
  };

  public postponeHistoryDashboardAnnounce = (postponeInMinutes: number) => {
    this.saveToClientStorage(
      historyDashboardAnnonucementPostponeKey,
      true,
      minutesToMilliseconds(postponeInMinutes)
    );
  };

  public stopHistoryDashboardAnnouncePermanently = () => {
    this.saveToClientStorage(
      historyDashboardAnnonucementPostponeKey,
      true,
      minutesToMilliseconds(60 * 24 * 1000000)
    );
  };

  public getHistoryDashboardAnnouncePostpone = () => {
    return this.getStoredItem(historyDashboardAnnonucementPostponeKey);
  };

  public setPWATutorialPostpone = (postponeInMinutes: number) => {
    this.saveToClientStorage(PWATutorialPostpone, true, minutesToMilliseconds(postponeInMinutes));
  };

  /*   public setETATutorialPostpone = (postponeInMinutes: number) => {
    this.saveToClientStorage(ETABannerPostpone,true, minutesToMilliseconds(postponeInMinutes));
  } */

  public setPWATutorialLater = () => {
    this.saveToClientStorage(PWATutorialPostpone, 'NOT_SET', minutesToMilliseconds(60 * 24 * 365));
  };

  public clearPWATutorialStatus = () => {
    this.removeStoredItem(PWATutorialPostpone);
  };

  public clearETACounter = () => {
    this.removeStoredItem(ETABannerPostponeCounter);
  };

  public getPWATutorialPostpone = () => {
    return this.getStoredItem(PWATutorialPostpone);
  };

  public getPWATutorialCounter = () => {
    return this.getStoredItem(PWATutorialPostponeCounter);
  };

  public getETATutorialCounter = () => {
    return this.getStoredItem(ETABannerPostponeCounter);
  };

  public increasePWATutorialCounter = () => {
    const updatedCount = (this.getStoredItem(PWATutorialPostponeCounter) || 0) + 1;
    this.saveToClientStorage(
      PWATutorialPostponeCounter,
      updatedCount,
      minutesToMilliseconds(60 * 24 * 365)
    );
  };

  public increaseETABannerCount = () => {
    const updateCount = (this.getStoredItem(ETABannerPostponeCounter) || 0) + 1;
    this.saveToClientStorage(
      ETABannerPostponeCounter,
      updateCount,
      minutesToMilliseconds(60 * 24 * 365)
    );
  };
}

export default new UtilStorageService();
